import { useCallback, useState } from 'react';
import { store } from '../../app/store';
import { extractErrorMessage } from '../../utils/errorUtils';
import { financeEmailHistoryActionApi } from '../grid/api/financeEmailHistoryActionApi';
import { isInboundFinanceEmailHistoryId } from '../grid/utils/financeEmailHistoryId';
import { financeEmailInboxApi } from './api/financeEmailInboxApi';

type MailboxEndpoint =
  'softDeleteFinanceEmails' | 'markFinanceEmailsSpam' | 'unmarkFinanceEmailsSpam';

type NotifySeverity = 'success' | 'error';

type UseFinanceEmailInboxMailboxActionsArgs = {
  requestConfirmation: (message: string) => Promise<boolean>;
  onNotify: (message: string, severity: NotifySeverity) => void;
  onAfterThreadRemoved?: (historyIds: string[]) => void;
};

const invalidateInbox = () => {
  store.dispatch(financeEmailInboxApi.util.invalidateTags(['FinanceEmailInbox']));
};

export function useFinanceEmailInboxMailboxActions({
  requestConfirmation,
  onNotify,
  onAfterThreadRemoved,
}: UseFinanceEmailInboxMailboxActionsArgs) {
  const [isActing, setIsActing] = useState(false);

  const runIdsAction = useCallback(
    async (
      historyIds: string[],
      endpoint: MailboxEndpoint,
      confirmMessage: string,
      successFallback: string
    ) => {
      const ids = historyIds.map((id) => id.trim()).filter(Boolean);
      if (ids.length === 0) {
        onNotify('No messages selected for this action.', 'error');
        return false;
      }

      const confirmed = await requestConfirmation(confirmMessage);
      if (!confirmed) return false;

      setIsActing(true);
      try {
        const message = await store
          .dispatch(financeEmailHistoryActionApi.endpoints[endpoint].initiate(ids))
          .unwrap();
        invalidateInbox();
        onNotify(message || successFallback, 'success');
        onAfterThreadRemoved?.(ids);
        return true;
      } catch (error) {
        onNotify(extractErrorMessage(error, 'Action failed.'), 'error');
        return false;
      } finally {
        setIsActing(false);
      }
    },
    [onAfterThreadRemoved, onNotify, requestConfirmation]
  );

  const archiveMessages = useCallback(
    (historyIds: string[]) =>
      runIdsAction(
        historyIds,
        'softDeleteFinanceEmails',
        historyIds.length > 1
          ? `Archive this conversation (${historyIds.length} messages)?`
          : 'Archive this conversation?',
        'Conversation moved to archive.'
      ),
    [runIdsAction]
  );

  const markSpamMessages = useCallback(
    async (historyIds: string[]) => {
      const inboundIds = historyIds.filter((id) => isInboundFinanceEmailHistoryId(id));
      if (inboundIds.length === 0) {
        onNotify('Only inbound emails can be moved to spam.', 'error');
        return false;
      }

      return runIdsAction(
        inboundIds,
        'markFinanceEmailsSpam',
        inboundIds.length > 1
          ? `Move this conversation to spam (${inboundIds.length} messages)?`
          : 'Move this conversation to spam?',
        'Conversation moved to spam.'
      );
    },
    [onNotify, runIdsAction]
  );

  const addSenderToSpamList = useCallback(
    async (historyId: string, fromAddress?: string | null) => {
      const id = historyId.trim();
      if (!isInboundFinanceEmailHistoryId(id)) {
        onNotify('Add sender to spam list is only available for inbound emails.', 'error');
        return false;
      }

      const confirmMessage = fromAddress
        ? `Add "${fromAddress}" to the auto-spam list and move this email to spam?`
        : 'Add this sender to the auto-spam list and move this email to spam?';

      const confirmed = await requestConfirmation(confirmMessage);
      if (!confirmed) return false;

      setIsActing(true);
      try {
        const message = await store
          .dispatch(
            financeEmailHistoryActionApi.endpoints.addFinanceEmailSenderToSpamList.initiate(id)
          )
          .unwrap();
        invalidateInbox();
        onNotify(message || 'Sender added to the spam list.', 'success');
        onAfterThreadRemoved?.([id]);
        return true;
      } catch (error) {
        onNotify(extractErrorMessage(error, 'Failed to add sender to spam list.'), 'error');
        return false;
      } finally {
        setIsActing(false);
      }
    },
    [onAfterThreadRemoved, onNotify, requestConfirmation]
  );

  return {
    isActing,
    archiveMessages,
    markSpamMessages,
    addSenderToSpamList,
  };
}
